import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Pagination,
  Alert,
  CircularProgress,
  Grid,
  Dialog,
  Tooltip
} from '@mui/material';
import { 
  Movie as MovieIcon, 
  PlayCircleOutline as PlayIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import NVRPlayback from '../../components/nvr/NVRPlayback';
import VideoPlayer from '../../components/nvr/VideoPlayer';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import api from '../../services/nvr/api';
import { api as mainApi } from '../../api';

// 탭 패널 컴포넌트
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const VideoAnalysis = ({ language }) => {
  const [value, setValue] = useState(0);
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDetections, setTotalDetections] = useState(0);
  
  // 필터 상태
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));
  
  // 비디오 플레이어 상태
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [loadingDetectionId, setLoadingDetectionId] = useState(null);

  const fetchDetections = async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pageNum,
        per_page: 20,
        sort_by: 'date_desc'
      };
      
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await mainApi.get('/api/detections/filtered/', { params });
      const data = response.data;
      
      if (data.detections) {
        setDetections(data.detections);
        setTotalPages(data.total_pages || 1);
        setPage(data.current_page || 1);
        setTotalDetections(data.total_records || 0);
      }
    } catch (err) {
      console.error('Error fetching detections:', err);
      setError('감지 기록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    if (value === 0) { // 동영상 분석 탭이 선택된 경우만
      fetchDetections();
    }
  }, [value]);

  // 영상 재생 핸들러
  const handlePlayVideo = async (detection) => {
    setLoadingDetectionId(detection.detection_id);
    try {
      const playbackData = {
        channel: parseInt(detection.camera_id) || 5,
        start_time: new Date(detection.detection_time).toISOString(),
        ip: '192.168.219.102',
        port: 80,
        username: 'admin',
        password: 'admin1234',
      };

      const response = await api.createPlaybackSession(playbackData);
      const session = response.data;
      
      setActiveSession(session);
      setVideoDialogOpen(true);
      toast.success(`감지 시간: ${dayjs(detection.detection_time).format('YYYY년 MM월 DD일 HH:mm:ss')} 영상을 재생합니다.`);
    } catch (error) {
      console.error('Video playback error:', error);
      toast.error('영상 재생 중 오류가 발생했습니다: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingDetectionId(null);
    }
  };

  // 영상 재생 중지
  const handleCloseVideo = async () => {
    if (activeSession) {
      try {
        await api.stopPlaybackSession(activeSession.id);
      } catch (error) {
        console.error('Error stopping playback:', error);
      }
    }
    setVideoDialogOpen(false);
    setActiveSession(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={value} 
          onChange={(e, newValue) => setValue(newValue)} 
          aria-label="video analysis tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 160,
              fontWeight: 600,
            }
          }}
        >
          <Tab icon={<MovieIcon />} iconPosition="start" label="동영상 분석" />
          <Tab icon={<PlayIcon />} iconPosition="start" label="영상 재생 시스템" />
        </Tabs>
      </Paper>

      <TabPanel value={value} index={0}>
        <Box>
          {/* 헤더 */}
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            감지 기록 분석
          </Typography>

          {/* 필터 섹션 */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="시작일"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="종료일"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  onClick={() => fetchDetections(1)}
                  startIcon={<FilterIcon />}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  필터 적용
                </Button>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="outlined"
                  onClick={() => fetchDetections(page)}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  새로고침
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* 통계 정보 */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="body1">
              총 {totalDetections}개의 감지 기록
            </Typography>
          </Paper>

          {/* 에러 메시지 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 감지 기록 테이블 */}
          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>감지 시간</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>바운딩 박스 수</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>카메라 ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>영상보기</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detections.length > 0 ? (
                      detections.map((detection) => (
                        <TableRow key={detection.detection_id} hover>
                          <TableCell>{detection.detection_id}</TableCell>
                          <TableCell>{dayjs(detection.detection_time).format('YYYY년 MM월 DD일 HH:mm:ss')}</TableCell>
                          <TableCell>{detection.bb_count}</TableCell>
                          <TableCell>{detection.camera_id}</TableCell>
                          <TableCell>
                            <Tooltip title={`${dayjs(detection.detection_time).format('YYYY년 MM월 DD일 HH:mm:ss')} 영상 재생`}>
                              <Box
                                sx={{
                                  width: 100,
                                  height: 56,
                                  backgroundColor: '#1a1a1a',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  cursor: loadingDetectionId ? 'not-allowed' : 'pointer',
                                  border: '2px solid transparent',
                                  position: 'relative',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    '& .play-overlay': {
                                      opacity: 1,
                                    }
                                  }
                                }}
                                onClick={() => !loadingDetectionId && handlePlayVideo(detection)}
                              >
                                {detection.thumbnail_url ? (
                                  <img
                                    src={detection.thumbnail_url}
                                    alt={`Detection ${detection.detection_id}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      opacity: 0.7,
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <MovieIcon sx={{ fontSize: 32, color: 'grey.600' }} />
                                )}
                                
                                <Box
                                  className="play-overlay"
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    opacity: 0.8,
                                    transition: 'opacity 0.2s',
                                  }}
                                >
                                  {loadingDetectionId === detection.detection_id ? (
                                    <CircularProgress size={24} color="primary" />
                                  ) : (
                                    <PlayArrowIcon sx={{ fontSize: 36, color: 'white' }} />
                                  )}
                                </Box>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          감지 기록이 없습니다.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <Pagination 
                      count={totalPages} 
                      page={page} 
                      onChange={(event, newPage) => fetchDetections(newPage)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </TableContainer>

          {/* 비디오 재생 다이얼로그 */}
          <Dialog
            open={videoDialogOpen}
            onClose={handleCloseVideo}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              }
            }}
          >
            {/* 비디오 플레이어 */}
            {activeSession && (
              <VideoPlayer
                session={activeSession}
                onClose={handleCloseVideo}
              />
            )}
          </Dialog>
        </Box>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <NVRPlayback />
      </TabPanel>
    </Box>
  );
};

export default VideoAnalysis; 